import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import { Card } from 'semantic-ui-react';
import moment from 'moment';
import { formatMoneyLegacy } from '../../utils/currency';
import { showSharedRental, deleteSharedRental, assignPartnerToSharedRental, unassignPartnerFromSharedRental, stopBatchSharedRental, stopSeriesSharedRental } from '../../api/sharedRentalsApi';
import { updateSharedRentalInterest, markInterestPaidSharedRental, markRestPaidSharedRental, sendRestPaymentLink, sendInterestLink, refundSharedRentalInterest, getInterestPaymentInfo, initiateRestPayment } from '../../api/sharedRentalInterestsApi';
import { getAllPartners } from '../../api/partnerApi';
import { createServiceTransaction } from '../../api/serviceTransactionApi';
import { BASE_URL } from '../../constants';
import { PaymentMethodModal } from '../payments';
import AdminRecordOfflinePaymentModal from './AdminRecordOfflinePaymentModal';
import { AdminPhaseBanner, GroupMemberBadge } from './AdminStatusBadge';
import {
  simpleGroupStatus,
  groupMemberDepositCell,
  groupMemberBalanceCell,
} from '../../utils/adminBookingStatus';
import '../../styles/admin-manage.scss';
import history from '../../history';

const PAYMENT_METHODS = [
  { value: 'offline_bank', label: 'Bank transfer' },
  { value: 'offline_cash', label: 'Cash' },
  { value: 'offline_cheque', label: 'Cheque' }
];

class SharedRentalDetails extends Component {
  state = {
    rental: null,
    updating: false,
    fetchError: false,
    editingMember: null,
    sendingLinkId: null,
    markingInterestIdx: null,
    refundingIdx: null,
    assignVendorOpen: false,
    assignVendorForm: { vendor_id: '' },
    partners: [],
    recordPaymentOpen: false,
    recordPaymentForm: { amount: '', payment_mode: 'offline_bank', receipt_no: '', remarks: '' },
    recording: false,
    unassigning: false,
    showPaymentModal: false,
    paymentModalData: null,
    offlinePaymentOpen: false,
    offlinePaymentContext: null,
    recordingOffline: false
  };

  fetchRental = () => {
    const idx = this.props.match.params.idx;
    this.setState({ fetchError: false });
    showSharedRental(idx)
      .then((r) => this.setState({ rental: r.data, fetchError: false }))
      .catch(() => this.setState({ rental: null, fetchError: true }));
  };

  componentDidMount() {
    const idx = this.props.match.params.idx;
    const s = this.props.location.state && this.props.location.state.sharedRental;
    if (s && s.idx === idx) { this.setState({ rental: s }); this.fetchRental(); return; }
    this.fetchRental();
  }

  destroy = (id) => {
    swal({ title: 'Are you sure?', text: 'This shared rental will be deleted.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (ok) {
          deleteSharedRental(id).then(() => { swal('Deleted', { icon: 'success' }); history.push('/admin/shared_rentals'); })
            .catch(() => swal('Delete failed', { icon: 'error' }));
        }
      });
  };

  openAdminPayInterest = (m) => {
    const rental = this.state.rental;
    getInterestPaymentInfo(m.idx)
      .then((r) => {
        const d = r.data || {};
        this.setState({
          showPaymentModal: true,
          paymentModalData: {
            productIdentity: m.idx,
            productName: 'SHARED_RENTAL',
            amount: Number(d.amount || m.amount || 0),
            currencyCode: rental?.currency_code || d.currency_code || 'NPR',
            productUrl: `${BASE_URL || window.location.origin}/admin/shared_rentals/${rental?.idx}`,
            customerName: m.name,
            customerEmail: m.email,
            customerPhone: m.phone,
          }
        });
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not load payment info.', 'error'));
  };

  openAdminPayRest = (m) => {
    const rental = this.state.rental;
    initiateRestPayment(m.idx)
      .then((r) => {
        if (r.data?.idx != null) {
          this.setState({
            showPaymentModal: true,
            paymentModalData: {
              productIdentity: r.data.idx,
              productName: 'SHARED_RENTAL_REST',
              amount: Number(r.data.amount || m.rest_amount || 0),
              currencyCode: rental?.currency_code || 'NPR',
              productUrl: `${BASE_URL || window.location.origin}/admin/shared_rentals/${rental?.idx}`,
              customerName: m.name,
              customerEmail: m.email,
              customerPhone: m.phone,
            }
          });
        }
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Could not start rest payment.', 'error'));
  };

  openOfflinePayment = (context) => {
    this.setState({ offlinePaymentOpen: true, offlinePaymentContext: context });
  };

  closeOfflinePayment = () => {
    this.setState({ offlinePaymentOpen: false, offlinePaymentContext: null, recordingOffline: false });
  };

  submitOfflinePayment = (offlinePayment) => {
    const ctx = this.state.offlinePaymentContext;
    if (!ctx) return;
    this.setState({ recordingOffline: true, updating: true });
    const promise = ctx.type === 'interest'
      ? markInterestPaidSharedRental(ctx.memberIdx, offlinePayment)
      : markRestPaidSharedRental(ctx.memberIdx, offlinePayment);
    promise
      .then(() => {
        swal('Recorded', ctx.type === 'interest' ? 'Interest marked as paid.' : 'Rest marked as paid.', 'success');
        this.closeOfflinePayment();
        this.fetchRental();
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Update failed', 'error'))
      .finally(() => this.setState({ recordingOffline: false, updating: false }));
  };

  markInterestPaid = (interestIdx) => {
    this.openOfflinePayment({ type: 'interest', memberIdx: interestIdx });
  };

  markRestPaid = (interestIdx) => {
    this.openOfflinePayment({ type: 'rest', memberIdx: interestIdx });
  };

  removeFromGroup = (interestIdx) => {
    swal({ title: 'Remove from group?', text: 'This member will be removed. The slot will be available again. Interest fee is not refunded.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        this.setState({ updating: true });
        updateSharedRentalInterest(interestIdx, { status: 'removed' })
          .then(() => { swal('Removed', { icon: 'success' }); this.fetchRental(); })
          .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Update failed', { icon: 'error' }))
          .finally(() => this.setState({ updating: false }));
      });
  };

  copyPaymentLink = (m) => {
    const rental = this.state.rental;
    const cc = rental && (rental.currency_code || 'NPR');
    const url = `${window.location.origin}/shared-rentals?pay_rest=${m.idx}`;
    const restStr = (m.rest_amount != null && m.rest_amount > 0) ? ` ${formatMoneyLegacy(m.rest_amount, cc)}` : '';
    navigator.clipboard.writeText(url).then(
      () => swal('Copied', `Payment link copied. Send this to ${m.name || 'the member'} to collect the rest${restStr}.`, 'success'),
      () => swal('Copy failed', `Link: ${url}`, 'info')
    );
  };

  copyInterestLink = (m) => {
    const rental = this.state.rental;
    const cc = rental && (rental.currency_code || 'NPR');
    const url = `${window.location.origin}/shared-rentals?pay_interest=${m.idx}`;
    const amtStr = (m.amount != null && m.amount > 0) ? ` ${formatMoneyLegacy(m.amount, cc)}` : '';
    navigator.clipboard.writeText(url).then(
      () => swal('Copied', `Interest payment link copied. Send this to ${m.name || 'the member'} to complete registration${amtStr}.`, 'success'),
      () => swal('Copy failed', `Link: ${url}`, 'info')
    );
  };

  sendPaymentLink = (m) => {
    this.setState({ sendingLinkId: m.idx });
    sendRestPaymentLink(m.idx)
      .then(() => { swal('Sent', 'Payment link sent to email and/or phone.', 'success'); this.fetchRental(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Send failed', 'error'))
      .finally(() => this.setState({ sendingLinkId: null }));
  };

  sendInterestPaymentLink = (m) => {
    this.setState({ sendingLinkId: m.idx });
    sendInterestLink(m.idx)
      .then(() => { swal('Sent', 'Interest payment link sent to email and/or phone.', 'success'); this.fetchRental(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Send failed', 'error'))
      .finally(() => this.setState({ sendingLinkId: null }));
  };

  triggerRefund = (m) => {
    this.setState({ refundingIdx: m.idx });
    refundSharedRentalInterest(m.idx)
      .then(() => {
        swal('Refund enqueued', 'Refund job has been queued. Status will update when processing completes.', 'success');
        this.fetchRental();
      })
      .catch((e) => {
        const msg = (e.response?.data?.errors || [])?.join(' ') || e.message || 'Refund request failed';
        swal('Error', msg, 'error');
      })
      .finally(() => this.setState({ refundingIdx: null }));
  };

  openEditMember = (m) => { this.setState({ editingMember: { ...m, rest_amount: (m.rest_amount_override != null && m.rest_amount_override !== '') ? m.rest_amount_override : null } }); };
  closeEditMember = () => { this.setState({ editingMember: null }); };

  openAssignVendor = () => {
    getAllPartners({ q: { status_eq: 'approved', for_assignment_type: 'SharedRental' } })
      .then((r) => {
        const list = (r.data && (Array.isArray(r.data) ? r.data : r.data.partners || r.data.vendors)) || [];
        this.setState({
          assignVendorOpen: true,
          partners: list,
          assignVendorForm: { vendor_id: this.state.rental?.vendor_id || '' }
        });
      })
      .catch(() => this.setState({ assignVendorOpen: true, partners: [] }));
  };
  closeAssignVendor = () => { this.setState({ assignVendorOpen: false }); };

  openRecordPayment = () => {
    const { rental } = this.state;
    const payout = (rental.vendor_payout != null && rental.vendor_payout > 0) ? rental.vendor_payout : (rental.vendor_amount != null && rental.vendor_amount > 0) ? rental.vendor_amount : null;
    const amount = payout != null ? String(Number(payout).toFixed(0)) : '';
    this.setState({ recordPaymentOpen: true, recordPaymentForm: { amount, payment_mode: 'offline_bank', receipt_no: '', remarks: '' } });
  };
  closeRecordPayment = () => this.setState({ recordPaymentOpen: false });
  recordPaymentSubmit = () => {
    const { rental, recordPaymentForm } = this.state;
    const amount = Number(recordPaymentForm.amount);
    if (!rental.vendor_id || !amount || amount <= 0) {
      swal('Invalid', 'Amount is required and must be greater than 0.', 'info');
      return;
    }
    this.setState({ recording: true });
    createServiceTransaction({
      vendor_id: rental.vendor_id,
      direction: 'debit',
      amount,
      assignment_type: 'SharedRental',
      assignment_idx: rental.idx,
      payment_mode: recordPaymentForm.payment_mode,
      receipt_no: recordPaymentForm.receipt_no || undefined,
      remarks: recordPaymentForm.remarks || undefined
    })
      .then(() => {
        swal('Recorded', 'Payment recorded for this rental. Vendor balance updated.', 'success');
        this.closeRecordPayment();
        this.fetchRental();
      })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed to record payment', 'error'))
      .finally(() => this.setState({ recording: false }));
  };

  assignVendorSubmit = () => {
    const { rental, assignVendorForm } = this.state;
    if (!rental || !assignVendorForm.vendor_id) {
      swal('Select a vendor', 'Partner is required.', 'info');
      return;
    }
    const slotClosed = rental.slot_closed === true;
    const doAssign = () => {
      this.setState({ updating: true });
      assignPartnerToSharedRental(rental.idx, { vendor_id: assignVendorForm.vendor_id, force_close: slotClosed ? undefined : true })
        .then(() => {
          swal('Assigned', 'Vendor assigned. Credit has been added to their ledger.', 'success');
          this.closeAssignVendor();
          this.fetchRental();
        })
        .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Assign failed', 'error'))
        .finally(() => this.setState({ updating: false }));
    };
    if (!slotClosed) {
      swal({ title: 'Slot still open', text: 'This slot is still open for registrations. Assigning a vendor will close registrations. Do you want to continue?', icon: 'warning', buttons: true })
        .then((ok) => { if (ok) doAssign(); });
    } else {
      doAssign();
    }
  };

  unassignVendor = () => {
    const { rental } = this.state;
    if (!rental?.idx) return;
    swal({ title: 'Unassign vendor?', text: 'The vendor will be removed from this rental. You can then assign a different vendor.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        this.setState({ unassigning: true });
        unassignPartnerFromSharedRental(rental.idx)
          .then(() => {
            swal('Done', 'Vendor unassigned. You can now assign a different vendor.', 'success');
            this.fetchRental();
          })
          .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Unassign failed', 'error'))
          .finally(() => this.setState({ unassigning: false }));
      });
  };

  handleStopBatch = (rental) => {
    if (!rental || !rental.idx) return;
    this.setState({ updating: true });
    stopBatchSharedRental(rental.idx)
      .then(() => { swal('Done', 'This batch will not create future batches.', 'success'); this.fetchRental(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  handleStopSeries = (rental) => {
    if (!rental || !rental.idx) return;
    swal({ title: 'Stop entire series?', text: 'No further batches will be created for this recurring rental. Existing batches remain unchanged.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        this.setState({ updating: true });
        stopSeriesSharedRental(rental.idx)
          .then(() => { swal('Done', 'Recurrence stopped for this series.', 'success'); this.fetchRental(); })
          .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
          .finally(() => this.setState({ updating: false }));
      });
  };

  saveEditMember = () => {
    const { editingMember } = this.state;
    if (!editingMember) return;
    this.setState({ updating: true });
    const payload = {
      name: editingMember.name,
      email: editingMember.email,
      phone: editingMember.phone,
      number_of_people: editingMember.number_of_people,
      rest_amount: (editingMember.rest_amount === '' || editingMember.rest_amount == null) ? null : Number(editingMember.rest_amount)
    };
    updateSharedRentalInterest(editingMember.idx, payload)
      .then(() => { swal('Updated', 'Member details saved.', 'success'); this.closeEditMember(); this.fetchRental(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Update failed', 'error'))
      .finally(() => this.setState({ updating: false }));
  };

  render() {
    const { rental, updating, fetchError } = this.state;
    if (fetchError) {
      return (
        <div className="container p-4">
          <div className="alert alert-warning">Shared rental not found or it has been deleted.</div>
          <Link to="/admin/shared_rentals" className="btn btn-outline-primary">Back to Shared Rentals</Link>
        </div>
      );
    }
    if (!rental) return <div className="container p-4">Loading...</div>;
    const members = rental.members || [];
    const people = (rental.paid_people_count != null ? rental.paid_people_count : rental.paid_interests_count) || 0;
    const regs = rental.paid_interests_count != null ? rental.paid_interests_count : 0;
    const peopleStr = (regs !== people && regs > 0) ? `${people} (${regs} reg.)` : `${people}`;
    const reservedCount = (rental.reserved_people_count != null ? rental.reserved_people_count : 0);

    return (
      <div className="container">
        <Card fluid>
          <Card.Content>
            <div className="d-flex justify-content-between flex-wrap">
              <h3 className="title">{rental.name || 'Shared Rental'}</h3>
              <div>
                {rental.has_any_booking ? (
                  <span className="text-muted small" title="Not available once anyone has registered">Edit / Delete locked</span>
                ) : (
                  <>
                    <Link to={{ pathname: `/admin/shared_rental_form/${rental.idx}`, state: { sharedRental: rental } }} className="btn btn-outline-primary">Edit</Link>
                    <span className="btn btn-outline-danger ml-2" onClick={() => this.destroy(rental.idx)}>Delete</span>
                  </>
                )}
              </div>
            </div>
            <Link to="/admin/manage_shared_rentals" className="btn btn-outline-secondary btn-sm mt-2 mb-2">← Manage Shared Rentals</Link>
            <Link to="/admin/shared_rentals" className="btn btn-outline-secondary btn-sm mt-2 mb-3 ml-2">All Shared Rentals</Link>
            <AdminPhaseBanner phase={simpleGroupStatus({ ...rental, group_formed: rental.rental_formed })} />
            {simpleGroupStatus({ ...rental, group_formed: rental.rental_formed }).key === 'waiting' && (
              <div className="alert alert-warning py-2 mb-3">Collect payments from members or assign a vendor.</div>
            )}
            <div className="mb-3">
              <strong>Vendor (partner)</strong>
              {rental.vendor_id ? (
                <>
                  <span className="ml-2">Assigned to {rental.vendor_company_name || `ID ${rental.vendor_id}`}{(rental.vendor_payout != null && rental.vendor_payout > 0) ? ` · Payout ${formatMoneyLegacy(rental.vendor_payout, rental.currency_code || 'NPR')}` : ''}</span>
                  {rental.vendor_completed_at ? (
                    <span className="ml-2 badge badge-success">Vendor marked complete · {moment(rental.vendor_completed_at).format('D MMM YYYY, h:mm a')}</span>
                  ) : (
                    <>
                      <span className="ml-2 badge badge-warning">Pending vendor confirmation</span>
                      <button type="button" className="btn btn-outline-warning btn-sm ml-2" onClick={this.unassignVendor} disabled={!!this.state.unassigning} title="Remove this vendor so you can assign someone else">
                        {this.state.unassigning ? 'Unassigning…' : 'Unassign'}
                      </button>
                    </>
                  )}
                  {rental.vendor_completed_at && <p className="text-muted small mt-1 mb-0">This listing belongs to the assigned vendor and cannot be reassigned.</p>}
                  {!rental.vendor_completed_at && <p className="text-muted small mt-1 mb-0">Use <strong>Unassign</strong> to assign a different vendor.</p>}
                </>
              ) : (
                <>
                  <span className="ml-2 text-muted">Not assigned</span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm ml-2"
                    onClick={this.openAssignVendor}
                    disabled={!!updating || !rental.fully_paid || !rental.rental_formed}
                    title={!rental.rental_formed ? 'Available when team is formed (min people met) and all have paid the rest' : !rental.fully_paid ? 'Available when all members have paid the rest' : undefined}
                  >
                    Assign vendor
                  </button>
                  {!rental.rental_formed && <span className="ml-2 text-muted small">(enable when team is formed and all have paid the rest)</span>}
                  {rental.rental_formed && !rental.fully_paid && <span className="ml-2 text-muted small">(enable when all have paid the rest)</span>}
                </>
              )}
              {rental.vendor_id && (
                <>
                  <div className="mt-2 p-2 bg-light rounded small">
                    <strong>This rental:</strong> Final price (per person) <strong>{rental.final_price != null ? formatMoneyLegacy(rental.final_price, rental.currency_code || 'NPR') : '—'}</strong>
                    {(rental.vendor_payout != null && rental.vendor_payout > 0) && <> · Vendor payout <strong>{formatMoneyLegacy(rental.vendor_payout, rental.currency_code || 'NPR')}</strong></>}
                    <> · Paid to vendor for this rental <strong>{rental.vendor_paid_total != null ? formatMoneyLegacy(rental.vendor_paid_total, rental.currency_code || 'NPR') : formatMoneyLegacy(0, rental.currency_code || 'NPR')}</strong></>
                  </div>
                  {(rental.vendor_payout != null && rental.vendor_paid_total != null && Number(rental.vendor_paid_total) >= Number(rental.vendor_payout)) ? (
                    <span className="badge badge-success mt-1">Paid</span>
                  ) : (
                    <button type="button" className="btn btn-outline-success btn-sm mt-1" onClick={this.openRecordPayment} disabled={!!updating || !!this.state.recording}>Record payment to vendor (for this rental)</button>
                  )}
                  <p className="text-muted small mt-1 mb-0">Or pay from <Link to="/admin/summaries">Vendor Payouts</Link>.</p>
                </>
              )}
            </div>
            {rental.template_id && (
              <div className="mb-3">
                <strong>Recurrence</strong>
                {rental.series_stopped ? (
                  <span className="ml-2 badge badge-secondary">Recurrence stopped for this series</span>
                ) : (
                  <>
                    {!rental.auto_create_next && <p className="text-muted small mt-1 mb-0">This batch will not create future batches.</p>}
                    <div className="mt-1">
                      <button type="button" className="btn btn-outline-secondary btn-sm mr-1" onClick={() => this.handleStopBatch(rental)} disabled={!!updating || !rental.auto_create_next}>Stop This Batch</button>
                      <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => this.handleStopSeries(rental)} disabled={!!updating}>Stop Entire Series</button>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="list-view mt-3">
              <div className="list"><span className="label">From → To</span><span className="value">{(rental.origin || '—')} → {(rental.destination || '—')}</span></div>
              <div className="list"><span className="label">Start date</span><span className="value">{rental.start_date ? moment(rental.start_date).format('D MMM YYYY') : '—'}</span></div>
              <div className="list"><span className="label">End date</span><span className="value">{rental.end_date ? moment(rental.end_date).format('D MMM YYYY') : '—'}</span></div>
              <div className="list"><span className="label">Vendor base (per person)</span><span className="value">{rental.vendor_total_price != null ? formatMoneyLegacy(rental.vendor_total_price, rental.currency_code || 'NPR') : '—'}</span></div>
              <div className="list"><span className="label">Markup %</span><span className="value">{rental.markup_percentage != null ? rental.markup_percentage : '—'}</span></div>
              <div className="list"><span className="label">Final price (per person)</span><span className="value">{rental.final_price != null ? formatMoneyLegacy(rental.final_price, rental.currency_code || 'NPR') : '—'}</span></div>
              <div className="list"><span className="label">Interest %</span><span className="value">{rental.interest_percentage != null ? rental.interest_percentage : '—'}</span></div>
              <div className="list"><span className="label">Interest amount (per person)</span><span className="value">{rental.interest_amount != null ? formatMoneyLegacy(rental.interest_amount, rental.currency_code || 'NPR') : '—'}</span></div>
              <div className="list"><span className="label">Remaining payment (per person)</span><span className="value">{(rental.remaining_payment != null && rental.remaining_payment > 0) ? formatMoneyLegacy(rental.remaining_payment, rental.currency_code || 'NPR') : '—'}</span></div>
              <div className="list"><span className="label">Platform margin</span><span className="value">{rental.platform_margin != null ? formatMoneyLegacy(rental.platform_margin, rental.currency_code || 'NPR') : '—'}</span></div>
              <div className="list"><span className="label">Vendor payout (per person)</span><span className="value">{rental.vendor_payout_per_person != null ? formatMoneyLegacy(rental.vendor_payout_per_person, rental.currency_code || 'NPR') : (rental.vendor_payout != null ? formatMoneyLegacy(rental.vendor_payout, rental.currency_code || 'NPR') : '—')}</span></div>
              <div className="list"><span className="label">People in group</span><span className="value">{peopleStr} / {rental.min_group_size} min{reservedCount > (people || 0) ? ` (${reservedCount} registered)` : ''}{rental.max_group_size != null ? ` (max ${rental.max_group_size})` : ''}{rental.spots_left != null ? ` · ${rental.spots_left} spots left` : ''}</span></div>
              <div className="list"><span className="label">Accepting registrations</span><span className="value">{rental.accepting_registrations !== false ? 'Yes' : 'No (full)'}</span></div>
              <div className="list"><span className="label">Published</span><span className="value">{String(!!rental.published)}</span></div>
            </div>

            {members.length > 0 && (
              <div className="mt-4">
                <h5 className="mb-2">Members</h5>
                <p className="text-muted small">Deposit = first payment. Balance = remaining amount after deposit. Use payment links or mark paid offline.</p>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone / WhatsApp</th>
                        <th>#</th>
                        <th>Deposit</th>
                        <th>Balance due</th>
                        <th>Payment status</th>
                        <th>Refund status</th>
                        <th>Refund action</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => {
                        const refundStatus = m.refund_status || (m.status === 'refunded' ? 'completed' : null);
                        const isRefundCompleted = refundStatus === 'completed';
                        const isRefundFailed = refundStatus === 'failed';
                        const showRefundNow = m.status === 'paid' && !isRefundCompleted && !isRefundFailed;
                        const showRetry = isRefundFailed;
                        const refundButtonDisabled = isRefundCompleted || refundStatus === 'pending';
                        const refundLabel = isRefundCompleted ? 'Refunded' : (showRetry ? 'Retry' : 'Refund now');
                        const isRefunding = this.state.refundingIdx === m.idx;
                        const showRefundAction = m.status === 'paid' || m.status === 'refunded' || isRefundFailed;
                        return (
                        <tr key={m.idx}>
                          <td>{m.name || '—'}</td>
                          <td>{m.email || '—'}</td>
                          <td>{m.phone || '—'}</td>
                          <td>{m.number_of_people != null ? m.number_of_people : 1}</td>
                          <td>{groupMemberDepositCell(m.status)}</td>
                          <td>{(m.rest_amount != null && m.rest_amount > 0) ? formatMoneyLegacy(m.rest_amount, rental.currency_code || 'NPR') : '—'}</td>
                          <td><GroupMemberBadge status={m.status} /> <span className="small text-muted">{groupMemberBalanceCell(m.status)}</span></td>
                          <td>
                            {refundStatus == null || refundStatus === '' ? (
                              '—'
                            ) : (
                              <span
                                className={`badge ${refundStatus === 'completed' ? 'badge-success' : refundStatus === 'failed' ? 'badge-danger' : 'badge-warning'}`}
                                title={refundStatus === 'failed' && m.refund_error ? m.refund_error : undefined}
                              >
                                {refundStatus === 'pending' ? 'Pending' : refundStatus === 'completed' ? 'Refunded' : refundStatus === 'failed' ? 'Failed' : refundStatus}
                              </span>
                            )}
                          </td>
                          <td>
                            {showRefundAction ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => this.triggerRefund(m)}
                                disabled={refundButtonDisabled || !!updating || isRefunding}
                                title={isRefundFailed && m.refund_error ? m.refund_error : undefined}
                              >
                                {isRefunding ? '…' : refundLabel}
                              </button>
                            ) : '—'}
                          </td>
                          <td>
                            <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => this.openEditMember(m)} disabled={!!updating || m.status === 'rest_paid'}>Edit</button>
                            {m.status === 'rest_paid' && (
                              <span className="badge badge-success">Fully Paid</span>
                            )}
                            {m.status === 'pending_payment' && (
                              <>
                                <button type="button" className="btn btn-sm btn-primary mr-1" onClick={() => this.openAdminPayInterest(m)} disabled={!!updating}>Pay now</button>
                                <button type="button" className="btn btn-sm btn-outline-success mr-1" onClick={() => this.markInterestPaid(m.idx)} disabled={!!updating || this.state.recordingOffline}>
                                  Record cash/bank
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-primary mr-1" onClick={() => this.copyInterestLink(m)}>Copy interest link</button>
                                <button type="button" className="btn btn-sm btn-outline-info mr-1" onClick={() => this.sendInterestPaymentLink(m)} disabled={!!updating || this.state.sendingLinkId === m.idx}>
                                  {this.state.sendingLinkId === m.idx ? 'Sending…' : 'Send interest link'}
                                </button>
                              </>
                            )}
                            {m.status === 'paid' && (
                              <>
                                {(m.rest_amount != null && m.rest_amount > 0) && (
                                  <>
                                    <button type="button" className="btn btn-sm btn-primary mr-1" onClick={() => this.openAdminPayRest(m)} disabled={!!updating}>Pay rest</button>
                                    <button type="button" className="btn btn-sm btn-outline-primary mr-1" onClick={() => this.copyPaymentLink(m)}>Copy payment link</button>
                                    <button type="button" className="btn btn-sm btn-outline-info mr-1" onClick={() => this.sendPaymentLink(m)} disabled={!!updating || this.state.sendingLinkId === m.idx}>
                                      {this.state.sendingLinkId === m.idx ? 'Sending…' : 'Send payment link'}
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-success mr-1" onClick={() => this.markRestPaid(m.idx)} disabled={!!updating || this.state.recordingOffline}>Record rest paid</button>
                                  </>
                                )}
                              </>
                            )}
                            {m.status !== 'rest_paid' && (
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => this.removeFromGroup(m.idx)} disabled={!!updating}>Remove from group</button>
                            )}
                          </td>
                        </tr>
                      ); })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {rental.description && <div className="mt-3"><strong>Description</strong><p className="text-muted">{rental.description}</p></div>}
          </Card.Content>
        </Card>

        {this.state.assignVendorOpen && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign vendor</h5>
                  <button type="button" className="close" onClick={this.closeAssignVendor}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small">Only available after team is formed (enough members paid). Payout is set by the pricing engine.</p>
                  {rental.vendor_payout != null && rental.vendor_payout > 0 && (
                    <p className="mb-2"><strong>You will receive {formatMoneyLegacy(rental.vendor_payout_per_person != null ? rental.vendor_payout_per_person : rental.vendor_payout, rental.currency_code || 'NPR')}</strong> (per person) when admin marks this rental complete.</p>
                  )}
                  <div className="form-group">
                    <label>Vendor (partner)</label>
                    <select className="form-control" value={this.state.assignVendorForm.vendor_id} onChange={(e) => this.setState({ assignVendorForm: { ...this.state.assignVendorForm, vendor_id: e.target.value } })}>
                      <option value="">Select vendor</option>
                      {(() => {
                        const { rental, partners } = this.state;
                        const creatorVendor = rental && rental.created_by_user_id != null && partners.length > 0
                          ? partners.find((p) => p.user_id != null && String(p.user_id) === String(rental.created_by_user_id))
                          : null;
                        const otherVendors = creatorVendor ? partners.filter((p) => p.id !== creatorVendor.id) : partners;
                        return (
                          <>
                            {creatorVendor && (
                              <optgroup label="Creator Vendor">
                                <option value={creatorVendor.id}>{creatorVendor.company_name || `${creatorVendor.first_name} ${creatorVendor.last_name}`}</option>
                              </optgroup>
                            )}
                            <optgroup label={creatorVendor ? 'Other Approved Vendors' : 'Vendors'}>
                              {otherVendors.map((p) => (
                                <option key={p.id} value={p.id}>{p.company_name || `${p.first_name} ${p.last_name}`}</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={this.closeAssignVendor}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={this.assignVendorSubmit} disabled={!!updating || !this.state.assignVendorForm.vendor_id}>Assign</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {this.state.recordPaymentOpen && rental && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Record payment to vendor (this rental)</h5>
                  <button type="button" className="close" onClick={this.closeRecordPayment}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted small">This payment will be linked to this shared rental. Vendor payout: {(rental.vendor_payout != null ? rental.vendor_payout : rental.vendor_amount) != null ? formatMoneyLegacy(rental.vendor_payout != null ? rental.vendor_payout : rental.vendor_amount, rental.currency_code || 'NPR') : formatMoneyLegacy(0, rental.currency_code || 'NPR')}.</p>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input type="number" min={0.01} step={0.01} className="form-control" value={this.state.recordPaymentForm.amount} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, amount: e.target.value } })} placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label>Payment method</label>
                    <select className="form-control" value={this.state.recordPaymentForm.payment_mode} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, payment_mode: e.target.value } })}>
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Receipt / Reference no.</label>
                    <input type="text" className="form-control" value={this.state.recordPaymentForm.receipt_no} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, receipt_no: e.target.value } })} placeholder="Optional" />
                  </div>
                  <div className="form-group">
                    <label>Remarks</label>
                    <input type="text" className="form-control" value={this.state.recordPaymentForm.remarks} onChange={(e) => this.setState({ recordPaymentForm: { ...this.state.recordPaymentForm, remarks: e.target.value } })} placeholder="Optional" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={this.closeRecordPayment}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={this.recordPaymentSubmit} disabled={!!this.state.recording || !this.state.recordPaymentForm.amount}>Record payment</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {this.state.editingMember && (
          <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit member</h5>
                  <button type="button" className="close" onClick={this.closeEditMember}><span>&times;</span></button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Name</label>
                    <input type="text" className="form-control" value={this.state.editingMember.name || ''} onChange={(e) => this.setState({ editingMember: { ...this.state.editingMember, name: e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" className="form-control" value={this.state.editingMember.email || ''} onChange={(e) => this.setState({ editingMember: { ...this.state.editingMember, email: e.target.value } })} />
                  </div>
                  <div className="form-group">
                    <label>Phone / WhatsApp</label>
                    <input type="text" className="form-control" value={this.state.editingMember.phone || ''} onChange={(e) => this.setState({ editingMember: { ...this.state.editingMember, phone: e.target.value } })} placeholder="e.g. 98xxxxxx" />
                  </div>
                  <div className="form-group">
                    <label>Number of people</label>
                    <input type="number" min={1} className="form-control" value={this.state.editingMember.number_of_people != null ? this.state.editingMember.number_of_people : ''} onChange={(e) => this.setState({ editingMember: { ...this.state.editingMember, number_of_people: e.target.value === '' ? null : parseInt(e.target.value, 10) } })} />
                  </div>
                  <div className="form-group">
                    <label>Rest amount</label>
                    <input type="number" min={0} step={1} className="form-control" value={this.state.editingMember.rest_amount != null && this.state.editingMember.rest_amount !== '' ? this.state.editingMember.rest_amount : ''} onChange={(e) => this.setState({ editingMember: { ...this.state.editingMember, rest_amount: e.target.value === '' ? null : e.target.value } })} placeholder={`Rental default: ${(this.state.rental && this.state.rental.rest_amount != null) ? formatMoneyLegacy(this.state.rental.rest_amount, this.state.rental.currency_code || 'NPR') : '—'}`} />
                    <small className="text-muted">Leave blank to use the rental default. Set a value to override for this member.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={this.closeEditMember}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={this.saveEditMember} disabled={!!updating}>Save</button>
                </div>
              </div>
            </div>
          </div>
        )}
        <PaymentMethodModal
          open={this.state.showPaymentModal}
          onClose={() => this.setState({ showPaymentModal: false, paymentModalData: null })}
          paymentParams={this.state.paymentModalData}
          amount={this.state.paymentModalData?.amount}
          onPaymentInitiate={() => this.fetchRental()}
        />
        <AdminRecordOfflinePaymentModal
          open={this.state.offlinePaymentOpen}
          onClose={this.closeOfflinePayment}
          onSubmit={this.submitOfflinePayment}
          title={this.state.offlinePaymentContext?.type === 'rest' ? 'Record rest payment (offline)' : 'Record interest payment (offline)'}
          submitting={this.state.recordingOffline}
        />
      </div>
    );
  }
}

export default SharedRentalDetails;
